import { type ActivityLogEntry } from '@ugin/shared';
import { Activity, ChevronDown, ChevronRight } from 'lucide-react';
import { AnimatePresence, motion } from 'motion/react';
import { useState } from 'react';

import { type WidgetComponent, type WidgetMeta } from '../_base/types';
import { WidgetContainer } from '../_base/WidgetContainer';

import { Badge } from '@/components/ui/badge';
import { useRecentLogs } from '@/hooks/useMeta';
import { cn, formatRelativeTime } from '@/lib/utils';


const meta: WidgetMeta = {
  id: 'log-stream',
  name: 'Activity',
  description: "What Ugin's been doing.",
  icon: Activity,
  defaultSize: { w: 6, h: 6 },
  category: 'system',
};

export const LogStreamWidget: WidgetComponent<{ limit?: number }> = ({ limit = 50 }) => {
  const { data, isLoading, error, refetch } = useRecentLogs(limit);
  const entries = data?.entries ?? [];

  return (
    <WidgetContainer
      meta={meta}
      icon={<Activity className="size-4" />}
      loading={isLoading}
      error={error ?? undefined}
      onRetry={() => void refetch()}
      empty={!isLoading && entries.length === 0}
      emptyTitle="Quiet so far"
      emptyDescription="When Ugin works — scrapes, classifications, deck imports — he'll log it here."
    >
      <ul className="space-y-1.5 font-mono text-xs">
        {entries.map((entry) => (
          <LogRow key={entry.id} entry={entry} />
        ))}
      </ul>
    </WidgetContainer>
  );
};

LogStreamWidget.meta = meta;

function LogRow({ entry }: { entry: ActivityLogEntry }) {
  const [open, setOpen] = useState(false);
  const hasDetails = entry.details !== null && entry.details !== undefined;
  return (
    <li className="rounded-md border border-ugin-border/30 bg-ugin-bg-deep/30">
      <button
        type="button"
        onClick={() => { setOpen((v) => !v); }}
        disabled={!hasDetails}
        className={cn(
          'flex w-full items-center gap-2 px-2.5 py-1.5 text-left transition-colors',
          hasDetails && 'hover:bg-ugin-surface/40',
        )}
        aria-expanded={open}
      >
        <span className="w-12 shrink-0 text-ugin-text-faint">{formatRelativeTime(entry.ts)}</span>
        <Badge variant={statusVariant(entry.status)} className="shrink-0 uppercase">
          {entry.status}
        </Badge>
        <span className="shrink-0 text-ugin-text-muted">{entry.skill ?? 'system'}</span>
        <span className="flex-1 truncate text-ugin-text" title={entry.message ?? entry.action}>
          {entry.message ?? entry.action}
        </span>
        {hasDetails ? (
          open ? (
            <ChevronDown className="size-3 text-ugin-text-faint" />
          ) : (
            <ChevronRight className="size-3 text-ugin-text-faint" />
          )
        ) : null}
      </button>
      <AnimatePresence>
        {open && hasDetails ? (
          <motion.pre
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.18 }}
            className="overflow-x-auto whitespace-pre-wrap break-words border-t border-ugin-border/30 bg-ugin-bg-deep/50 px-3 py-2 text-[11px] text-ugin-text-muted"
          >
            {JSON.stringify(entry.details, null, 2)}
          </motion.pre>
        ) : null}
      </AnimatePresence>
    </li>
  );
}

function statusVariant(status: string): 'success' | 'warn' | 'danger' | 'default' {
  if (status === 'ok') return 'success';
  if (status === 'warn') return 'warn';
  if (status === 'error') return 'danger';
  return 'default';
}
